import { TimeSlot, DaySchedule, ScheduleData } from './types'
import { isValidTimeFormat, isValidTimeRange, timeToMinutes } from './timeUtils'

export interface ValidationResult {
  isValid: boolean
  errors: string[]
}

export class ScheduleValidator {
  static validateTimeSlot(slot: TimeSlot): ValidationResult {
    const errors: string[] = []

    if (slot.allDay) {
      return { isValid: true, errors: [] }
    }

    if (!slot.startTime || !isValidTimeFormat(slot.startTime)) {
      errors.push('Invalid start time format')
    }

    if (!slot.endTime || !isValidTimeFormat(slot.endTime)) {
      errors.push('Invalid end time format')
    }

    if (
      slot.startTime &&
      slot.endTime &&
      !isValidTimeRange(
        slot.startTime,
        slot.startPeriod,
        slot.endTime,
        slot.endPeriod,
      )
    ) {
      errors.push('End time must be after start time')
    }

    // Check if time range crosses midnight (but allow ending at midnight)
    if (slot.startTime && slot.endTime) {
      const startMinutes = timeToMinutes(slot.startTime, slot.startPeriod)
      const endMinutes = timeToMinutes(slot.endTime, slot.endPeriod, true)

      // If start time is later than end time (and end time isn't midnight), it crosses midnight
      if (startMinutes > endMinutes && endMinutes !== 24 * 60) {
        errors.push(
          'Time ranges cannot cross midnight. Please split the range across days.',
        )
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  static validateDaySchedule(day: DaySchedule): ValidationResult {
    const errors: string[] = []

    if (!day.enabled) {
      return { isValid: true, errors: [] }
    }

    // Validate each time slot
    day.timeSlots.forEach((slot, index) => {
      const slotValidation = this.validateTimeSlot(slot)
      if (!slotValidation.isValid) {
        errors.push(`Slot ${index + 1}: ${slotValidation.errors.join(', ')}`)
      }
    })

    // Check for overlapping slots
    if (this.hasOverlappingSlots(day.timeSlots)) {
      errors.push('Time slots overlap')
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  static validateSchedule(schedule: ScheduleData): ValidationResult {
    const errors: string[] = []

    schedule.days.forEach((day: DaySchedule) => {
      const dayValidation = this.validateDaySchedule(day)
      if (!dayValidation.isValid) {
        errors.push(`${day.label}: ${dayValidation.errors.join(', ')}`)
      }
    })

    return {
      isValid: errors.length === 0,
      errors,
    }
  }

  private static hasOverlappingSlots(slots: TimeSlot[]): boolean {
    const filtered = slots.filter(s => !s.allDay)

    for (let i = 0; i < filtered.length; i += 1) {
      const a = filtered[i]
      const aStart = timeToMinutes(a.startTime, a.startPeriod)
      const aEnd = timeToMinutes(a.endTime, a.endPeriod, true)

      for (let j = i + 1; j < filtered.length; j += 1) {
        const b = filtered[j]
        const bStart = timeToMinutes(b.startTime, b.startPeriod)
        const bEnd = timeToMinutes(b.endTime, b.endPeriod, true)

        if (aStart < bEnd && bStart < aEnd) {
          return true
        }
      }
    }
    return false
  }
}
